import type {
  AllocationStatus,
  ApprovalState,
  BillingType,
  ClientStatus,
  EngagementType,
  GoverningDocumentType,
  Practice,
  ProjectStatus,
  ProjectType,
  TaskPriority,
  TaskStatus,
  WritableBillableStatus,
} from '@repo/contracts';

export { SYSTEM_ROLE_LABELS, systemRoleLabel } from '@repo/contracts';

export const CLIENT_STATUS_LABELS: Record<ClientStatus, string> = {
  prospect: 'Prospect',
  active: 'Active',
  inactive: 'Inactive',
};

/**
 * One status-tone mapping, shared with PROJECT_STATUS_TONES and
 * BILLABLE_STATUS_TONES below: active/approved = success, pending/awaiting =
 * warning, delayed/rejected = danger, completed/archived/inactive = neutral.
 * Each Badge variant name is verified for contrast in packages/ui — pages
 * import this map rather than choosing their own tones.
 */
export const CLIENT_STATUS_TONES: Record<ClientStatus, 'success' | 'warning' | 'neutral'> = {
  active: 'success',
  prospect: 'warning',
  inactive: 'neutral',
};

export const PROJECT_STATUS_LABELS: Record<ProjectStatus, string> = {
  yet_to_start: 'Yet to start',
  delayed: 'Delayed',
  active: 'Active',
  completed: 'Completed',
};

export const PROJECT_STATUS_TONES: Record<
  ProjectStatus,
  'success' | 'warning' | 'danger' | 'neutral'
> = {
  active: 'success',
  delayed: 'danger',
  yet_to_start: 'warning',
  completed: 'neutral',
};

/**
 * Chart-only status colours, deliberately not the same tones as the badge
 * mapping above: a bar chart reads status as a categorical hue (active =
 * royal blue, delayed = warm amber) while a status pill reads it as a
 * severity colour (delayed = danger red). Both describe the same data
 * honestly, for different chart types.
 */
export const PROJECT_STATUS_CHART_COLORS: Record<ProjectStatus, string> = {
  active: 'var(--chart-status-active)',
  delayed: 'var(--chart-status-delayed)',
  yet_to_start: 'var(--chart-status-pending)',
  completed: 'var(--chart-status-done)',
};

export const ENGAGEMENT_TYPE_LABELS: Record<EngagementType, string> = {
  staffing: 'Staffing',
  co_managed: 'Co-managed',
  cyrino_managed: 'Cyrino Managed',
};

export const PROJECT_TYPE_LABELS: Record<ProjectType, string> = {
  development: 'Development',
  discovery: 'Discovery',
  consulting: 'Consulting',
  maintenance_and_support: 'Maintenance and Support',
  testing: 'Testing',
  poc: 'POC',
};

export const PRACTICE_LABELS: Record<Practice, string> = {
  data: 'Data',
  ai: 'AI',
  engineering: 'Engineering',
  cloud: 'Cloud',
  consulting: 'Consulting',
  devops: 'Devops',
};

export const BILLING_TYPE_LABELS: Record<BillingType, string> = {
  time_and_material: 'Time and material',
  fixed_monthly: 'Fixed monthly',
  fixed_milestone: 'Fixed milestone',
};

export const TASK_STATUS_LABELS: Record<TaskStatus, string> = {
  backlog: 'Backlog',
  todo: 'To do',
  in_progress: 'In progress',
  in_review: 'In review',
  done: 'Done',
  cancelled: 'Cancelled',
};

export const TASK_BOARD_COLUMNS: TaskStatus[] = ['todo', 'in_progress', 'in_review', 'done'];

export const TASK_PRIORITY_LABELS: Record<TaskPriority, string> = {
  low: 'Low',
  medium: 'Medium',
  high: 'High',
  urgent: 'Urgent',
};

export const TASK_PRIORITY_TONES: Record<TaskPriority, string> = {
  low: 'bg-muted text-muted-foreground',
  medium: 'bg-primary-soft text-primary',
  high: 'bg-warning/6 text-warning dark:bg-warning/18',
  urgent: 'bg-destructive/12 text-destructive',
};

/** Writes only ever carry these two; `internal` and `overhead` rows are read as non-billable. */
export const BILLABLE_STATUS_LABELS: Record<WritableBillableStatus, string> = {
  billable: 'Billable',
  non_billable: 'Non-billable',
};

export const BILLABLE_STATUS_TONES: Record<WritableBillableStatus, 'info' | 'neutral'> = {
  billable: 'info',
  non_billable: 'neutral',
};

export const ALLOCATION_STATUS_LABELS: Record<AllocationStatus, string> = {
  planned: 'Planned',
  active: 'Active',
  completed: 'Completed',
  cancelled: 'Cancelled',
};

export const ALLOCATION_STATUS_TONES: Record<
  AllocationStatus,
  'success' | 'info' | 'neutral' | 'danger'
> = {
  planned: 'info',
  active: 'success',
  completed: 'neutral',
  cancelled: 'danger',
};

export const APPROVAL_STATE_LABELS: Record<ApprovalState, string> = {
  pending: 'Pending approval',
  approved: 'Approved',
  rejected: 'Rejected',
};

export const APPROVAL_STATE_TONES: Record<ApprovalState, 'success' | 'warning' | 'danger'> = {
  approved: 'success',
  pending: 'warning',
  rejected: 'danger',
};

export const GOVERNING_DOCUMENT_TYPE_LABELS: Record<GoverningDocumentType, string> = {
  purchase_order: 'Purchase order',
  sow: 'Signed SOW',
  sow_in_progress: 'SOW in progress',
  approved_client_email: 'Approved client email',
};

/** Display label for the group of active people with no department on record. */
export const NO_DEPARTMENT_LABEL = 'No department';
